import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Clock3, Users } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import PageHero from "../components/PageHero";
import { TORONTO_WATERFRONT_IMAGE } from "../lib/brand";

function parseUTC(value) {
  if (!value) return new Date(NaN);
  const hasTimeZone = /[Zz]|[+-]\d{2}:?\d{2}$/.test(value);
  return new Date(hasTimeZone ? value : `${value}Z`);
}

function formatDate(value, lang, timeZone) {
  try {
    return parseUTC(value).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      ...(timeZone ? { timeZone } : {}),
    });
  } catch {
    return value;
  }
}

export default function Events() {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEvent, setOpenEvent] = useState(null);
  const localTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);

  useEffect(() => {
    api
      .get("/events?upcoming=true")
      .then((response) => setEvents(Array.isArray(response.data) ? response.data : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-alabaster" data-testid="events-page">
      <PageHero
        eyebrow={t("nav.events")}
        title={t("events.title")}
        subtitle={t("events.subtitle")}
        image={TORONTO_WATERFRONT_IMAGE}
        imageAlt={lang === "fr" ? "Toronto, lieu de référence pour les horaires des événements JNIC" : "Toronto, reference timezone for JNIC events"}
        compact
      >
        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs text-white/70">
          <Clock3 size={14} className="text-gold" />
          {lang === "fr" ? `Les heures sont adaptées à votre fuseau : ${localTimeZone}` : `Times are adapted to your timezone: ${localTimeZone}`}
        </div>
      </PageHero>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[0, 1, 2].map((item) => <div key={item} className="h-80 rounded-3xl bg-cream animate-pulse" />)}
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-3xl border border-navy/8 bg-white p-10 text-center shadow-sm">
            <Calendar size={32} className="mx-auto text-gold mb-5" />
            <p className="font-serif text-3xl text-navy">{t("events.none")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((event, index) => (
              <motion.article
                key={event.event_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                data-testid={`event-card-${event.event_id}`}
                className="group relative overflow-hidden bg-white rounded-[1.75rem] border border-navy/8 p-7 shadow-[0_16px_45px_rgba(6,22,42,.055)] hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(6,22,42,.11)] transition-all"
              >
                <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-gold/10" />
                <div className="relative flex items-start justify-between mb-7">
                  <span className={`text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 rounded-full ${event.kind === "webinar" ? "bg-cream text-redcanada" : "bg-navy/5 text-navy"}`}>
                    {event.kind}
                  </span>
                  <span className="text-xs text-navy/55 inline-flex items-center gap-1.5">
                    <Users size={13} /> {event.registrations}/{event.capacity}
                  </span>
                </div>

                <p className="relative text-[10px] tracking-[0.22em] uppercase text-redcanada mb-3 inline-flex items-center gap-2">
                  <Calendar size={13} /> {formatDate(event.date, lang, localTimeZone)}
                </p>
                <p className="relative text-[11px] text-navy/45 mb-5 leading-relaxed">
                  {lang === "fr" ? "Votre heure locale" : "Your local time"} · Toronto: {formatDate(event.date, lang, "America/Toronto")}
                </p>

                <h3 className="relative font-serif text-3xl text-navy leading-tight mb-3">{lang === "fr" ? event.title_fr : event.title_en}</h3>
                <p className="relative text-navy/60 text-sm leading-relaxed min-h-[4.8rem]">{lang === "fr" ? event.description_fr : event.description_en}</p>

                <button
                  onClick={() => setOpenEvent(event)}
                  data-testid={`event-register-${event.event_id}`}
                  className="relative mt-7 inline-flex items-center gap-2 text-sm font-semibold text-redcanada"
                >
                  {t("events.register")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </motion.article>
            ))}
          </div>
        )}
      </section>

      {openEvent && <RegisterModal event={openEvent} onClose={() => setOpenEvent(null)} />}
    </div>
  );
}

function RegisterModal({ event, onClose }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (submitEvent) => {
    submitEvent.preventDefault();
    setBusy(true);
    try {
      await api.post("/events/register", { event_id: event.event_id, ...form });
      setDone(true);
    } catch (error) {
      alert(error.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="event-register-modal">
      <motion.div
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-alabaster rounded-[2rem] max-w-md w-full p-8 relative shadow-2xl"
      >
        <button onClick={onClose} className="absolute top-5 right-5 text-navy/40 hover:text-navy" aria-label="Close" data-testid="event-modal-close">✕</button>
        {done ? (
          <div className="text-center py-8" data-testid="event-registered">
            <div className="mx-auto w-14 h-14 rounded-full bg-redcanada text-white grid place-items-center text-2xl mb-5">✓</div>
            <p className="font-serif text-4xl text-navy mb-3">{lang === "fr" ? "Inscription confirmée" : "Registration confirmed"}</p>
            <p className="text-navy/65">{lang === "fr" ? "Vérifiez vos courriels pour la suite." : "Check your email for the next steps."}</p>
            <button onClick={onClose} className="mt-7 px-6 py-3 rounded-full bg-navy text-white text-sm">OK</button>
          </div>
        ) : (
          <>
            <p className="text-xs tracking-[0.25em] uppercase text-redcanada mb-2">{t("events.register")}</p>
            <h3 className="font-serif text-3xl text-navy mb-6">{lang === "fr" ? event.title_fr : event.title_en}</h3>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder={lang === "fr" ? "Prénom" : "First name"} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="form-control" data-testid="event-form-firstname" />
                <input required placeholder={lang === "fr" ? "Nom" : "Last name"} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="form-control" data-testid="event-form-lastname" />
              </div>
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="form-control" data-testid="event-form-email" />
              <input placeholder={lang === "fr" ? "Téléphone (facultatif)" : "Phone (optional)"} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="form-control" data-testid="event-form-phone" />
              <button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-redcanada text-white text-sm font-semibold hover:bg-navy transition disabled:opacity-50" data-testid="event-form-submit">
                {busy ? t("common.loading") : t("events.register")}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
